import { getBrand, type BrandId } from "./brandRegistry";
import {
  DEFAULT_SNIPPET_STYLE_PROFILE,
  snippetTypeEnumFor,
  type SnippetStyleProfile,
} from "./snippetStyle";

export const SNIPPET_GUIDANCE_STORAGE_KEY = "finster_enable_snippet_guidance";
export const SNIPPET_TYPES_ALLOW_STORAGE_KEY = "finster_snippet_types";
export const SNIPPET_TYPES_DENY_STORAGE_KEY = "finster_snippet_types_deny";

export interface ResolvedSnippetPolicy {
  enabled: boolean;
  typeEnum: string[];
  profile: SnippetStyleProfile;
  storageOverride: boolean | null;
}

function readLocalStorage(key: string): string | null {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function parseCommaList(raw: string | null): string[] | undefined {
  if (!raw) return undefined;
  const items = raw
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  return items.length > 0 ? items : undefined;
}

function readEnabledOverride(): boolean | null {
  const raw = readLocalStorage(SNIPPET_GUIDANCE_STORAGE_KEY);
  if (raw === "true") return true;
  if (raw === "false") return false;
  return null;
}

export function resolveSnippetPolicy(input: {
  brandId?: BrandId | null;
  explicitEnabled?: boolean;
}): ResolvedSnippetPolicy {
  const brand = getBrand(input.brandId);
  const override = readEnabledOverride();
  const enabled =
    override ??
    (typeof input.explicitEnabled === "boolean"
      ? input.explicitEnabled
      : brand?.snippetsEnabled === true);

  const typeEnum = snippetTypeEnumFor(brand?.extraSnippetTypes, {
    allow: parseCommaList(readLocalStorage(SNIPPET_TYPES_ALLOW_STORAGE_KEY)),
    deny: parseCommaList(readLocalStorage(SNIPPET_TYPES_DENY_STORAGE_KEY)),
  });

  return {
    enabled,
    typeEnum,
    profile: brand?.snippetStyleProfile ?? DEFAULT_SNIPPET_STYLE_PROFILE,
    storageOverride: override,
  };
}
